import { ExperienceItem, ProjectItem, SkillCategory, SocialLink, EducationItem } from './types';

export const PERSONAL_INFO = {
  name: "Hamlet Hayrapetyan",
  title: "Motion Designer · Creative Technologist · 3D Generalist",
  location: "Los Angeles, CA",
  phone: "(626) 390-7247",
  email: "info@hamlt.com",
  summary: "I’m a motion designer and animator based in Los Angeles, with more than 10 years in 2D and 3D animation for live events, LED and projection stages, XR, broadcast and interactive work, for brands including Disney, NBCUniversal, Blizzard, Xbox, Google and Amazon.\n\nI also design and ship production software: Preshow, a client-review platform for LED content; Farmly, the render farm manager running Toy Robot Media’s 29-machine farm; and Henry’s Journey, a 3D puzzle game on web and iOS. I build with AI coding tools and own the product, architecture and testing decisions.",
  avatarUrl: "/profile.jpg" // Expects a file named 'profile.jpg' in the public/root folder
};

export const SHORT_BIO = "Hi, my name is Hamlet. With over 10 years of experience as a Motion Designer and Animator, I specialize in creating visuals that engage and inspire. My background spans 3D and 2D animation, large-scale live events, XR experiences, and broadcast content. I am proficient in industry-standard tools such as After Effects, Cinema 4D, Unreal Engine, and actively explore AI-driven tools to push creative boundaries.";

export const SOCIAL_LINKS: SocialLink[] = [
  { name: 'Vimeo', url: 'https://vimeo.com/hamLT', icon: 'Video' },
  { name: 'Behance', url: 'https://www.behance.net/hamLT', icon: 'Image' },
  { name: 'Dribbble', url: 'https://dribbble.com/hamLT', icon: 'Dribbble' },
  { name: 'LinkedIn', url: 'https://www.linkedin.com/in/hamLT/', icon: 'Linkedin' },
  { name: 'Rive', url: 'https://rive.app/marketplace/20370-38299-frog/', icon: 'Box' },
  { name: 'Hobby', url: 'http://hamlt.com', icon: 'Globe' },
];

export const EXPERIENCE: ExperienceItem[] = [
  {
    company: "Freelance",
    role: "Motion Graphics Designer & Animator",
    shortDescription: "Motion Graphics, 3D Animation & XR",
    location: "Pasadena, CA",
    period: "Dec 2024 - Present",
    description: [
      "Delivering high-quality motion graphics, 2D/3D animations, and XR content for agencies and direct clients.",
      "Creating content for live events, digital campaigns, branded media, and explainer videos.",
      "Combining traditional tools with AI platforms to enhance visual storytelling and workflow efficiency."
    ]
  },
  {
    company: "Mousetrappe Media",
    role: "Motion Graphics Designer",
    shortDescription: "Immersive Projection & Live-to-Film",
    location: "Burbank, CA",
    period: "May 2019 - Present",
    description: [
      "Created 2D and 3D animations for immersive live-to-film productions, including The Little Mermaid Live at the Hollywood Bowl and Not-So-Spooky Spectacular at Walt Disney World."
    ]
  },
  {
    company: "Toy Robot Media",
    role: "Motion Graphics Designer & Animator",
    shortDescription: "Large-scale Events & Broadcast Pipelines",
    location: "Pasadena, CA",
    period: "Feb 2020 - Nov 2024",
    description: [
      "Developed and delivered extensive, high-quality content for large-scale corporate events under tight deadlines, handling complex broadcast pipelines and multi-screen configurations.",
      "Created a wide range of assets, from cinematic show openers to code-automated slides.",
      "Collaborated closely with executive and creative teams, building and organizing extensive project repositories for prominent clients, including GM, Amazon, NBCUniversal, RSA, Blizzard, Google, TikTok, John Deere, Disney, Xbox, Twitch, and others."
    ]
  },
  {
    company: "Logistar",
    role: "Motion Graphics Designer",
    shortDescription: "Marketing & Corporate Communications",
    location: "Vernon, CA",
    period: "May 2019 - Feb 2020",
    description: [
      "Produced explainer videos, corporate presentations, and instructional videos, translating complex ideas into visually engaging content.",
      "Developed HTML5 banners and GIF animations for digital marketing, contributing to increased audience engagement.",
      "Edited video content for multi-channel distribution, ensuring high production standards and brand consistency."
    ]
  },
  {
    company: "Promote",
    role: "Motion Graphics Designer",
    shortDescription: "Digital Media & Template Automation",
    location: "Beverly Hills, CA",
    period: "July 2018 - Jan 2019",
    description: [
      "Designed and animated HTML5 assets for digital media, enhancing brand visibility and engagement.",
      "Created After Effects templates for mass distribution, improving workflow efficiency and content scalability.",
      "Produced explainer and product demo videos, supporting digital marketing strategies."
    ]
  },
  {
    company: "Equiti Group",
    role: "Motion Graphics Designer",
    shortDescription: "Brand Messaging & Social Media",
    location: "Los Angeles, CA", 
    period: "Apr 2017 - May 2018",
    description: [
      "Created explainer videos, product presentations, and instructional content to support brand messaging.",
      "Produced GIF animations and HTML5 banners for digital platforms, enhancing social media campaigns.",
      "Edited video content for brand consistency and optimized engagement."
    ]
  },
  {
    company: "BetConstruct",
    role: "3D Generalist, Motion Graphics Designer",
    shortDescription: "Game Development & 3D Visualization",
    location: "Yerevan, Armenia",
    period: "Nov 2015 - Apr 2017",
    description: [
      "Designed, animated, and edited 2D and 3D visual elements for game development and marketing materials.",
      "Produced product explainer videos, helping to communicate complex concepts effectively."
    ]
  }
];

export const EDUCATION: EducationItem[] = [
  {
    school: "State Engineering University of Armenia",
    degree: "Bachelor's degree",
    period: "Sep 2001 - Jul 2005"
  }
];

export const SKILLS: SkillCategory[] = [
  {
    title: "Skills",
    items: ["Motion Design", "2D/3D Animation", "3D Generalist", "Extended Reality (XR)", "Interactive Experiences", "Art Direction", "UI Animation", "Video Editing", "AI Integration"]
  },
  {
    title: "Tools",
    items: ["After Effects", "Cinema 4D", "3Ds Max", "Maya", "Unreal Engine", "Notch Builder", "Premiere Pro", "Photoshop", "Illustrator", "Rive", "Figma"]
  }
];

export const PROJECTS: ProjectItem[] = [
  {
    id: "showreel-2026",
    title: "Showreel 2026",
    category: "Vimeo / Reel",
    imageUrl: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=2064&auto=format&fit=crop", 
    link: "https://vimeo.com/1025804616",
    description: "A compilation of selected works from the past year showcasing motion, 3D, and compositing.",
    vimeoId: "1025804616" 
  },
  {
    id: "vimeo-portfolio",
    title: "Vimeo Portfolio",
    category: "Motion Design",
    imageUrl: "https://images.unsplash.com/photo-1626544827763-d516dce335ca?q=80&w=2070&auto=format&fit=crop", 
    link: "https://vimeo.com/hamLT",
    description: "Explore my latest motion design work and animations on Vimeo.",
  },
  {
    id: "behance-projects",
    title: "Behance Projects",
    category: "Case Studies",
    imageUrl: "https://images.unsplash.com/photo-1558655146-d09347e92766?q=80&w=2064&auto=format&fit=crop", 
    link: "https://www.behance.net/hamLT",
    description: "Deep dive into styleframes, process breakdowns, and art direction.",
  },
  {
    id: "dribbble-shots",
    title: "Dribbble Shots",
    category: "Snapshots / UI",
    imageUrl: "https://images.unsplash.com/photo-1611162617474-5b21e879e113?q=80&w=1974&auto=format&fit=crop",
    link: "https://dribbble.com/hamLT",
    description: "Daily renders, UI interactions, and motion snippets.",
  },
  {
    id: "henrys-journey",
    title: "Henry's Journey",
    category: "Game Design",
    imageUrl: "https://images.unsplash.com/photo-1552820728-8b83bb6b773f?q=80&w=2070&auto=format&fit=crop", 
    link: "https://henrysjourney.app/",
    description: "An interactive game experience designed and built from scratch.",
  },
  {
    id: "rive-animation",
    title: "Interactive Frog",
    category: "Rive / Interactive",
    imageUrl: "https://images.unsplash.com/photo-1534251623126-209a87d6bb1b?q=80&w=2070&auto=format&fit=crop",
    link: "https://rive.app/marketplace/20370-38299-frog/",
    description: "High-performance interactive animation built for the Rive marketplace."
  }
];

export const COVER_LETTER = `Dear Hiring Team,

I'm a motion designer and animator with more than ten years in 2D and 3D animation for live
events, LED and projection stages, XR, broadcast and interactive work, for brands including
Disney, NBCUniversal, Blizzard, Xbox, Google and Amazon.

That work runs from stadium-scale show openers and code-automated slides at Toy Robot Media
to live-to-film productions like The Little Mermaid Live at the Hollywood Bowl with Mousetrappe
Media. Most recently I created Cinema 4D and Redshift content for Chevron's booth at the LA
Auto Show, built to LED specs, and Rive state-machine animation for in-game UI.

What I'd add to your team is the ability to build the tools around the work as well as the work
itself. I design and ship production software, all of it usable on a phone:

Preshow (preshow.link) lets clients review video on LED screens inside a 3D model of the stage,
before load-in: a browser 3D editor, versioned media, review links, a public API and billing.

Farmly replaced Deadline on Toy Robot Media's 29-machine render farm. It ran beside Deadline
with zero failed renders, then took over the farm.

Stitchr lists a secondhand shop's items once and sells them on five marketplaces, with a native
iPhone app and home-screen widget.

Henry's Journey (henrysjourney.app) is a 3D puzzle game on web and iOS, with solver-verified
levels and a level editor.

I build these with AI coding tools and make the product, architecture and testing decisions
myself. In practice that means I can prototype an interactive idea in a day, automate the
repetitive part of a pipeline, or talk to engineers in their own terms when a motion piece has to
run in real time.

I'm organized, dependable and comfortable taking a project from concept to delivery on my own
or inside a larger team. My reel and case studies are at motion.hamlt.com. I'd welcome the
chance to talk about how I can help yours.

Thank you,
Hamlet Hayrapetyan`;

